import Column from '@app/components/layout/Column'
import { Breadcrumbs } from '@app/components/core/breadcrumbs/Breadcrumbs'
import { InitiativeViewPageLayout } from '@app/pages/layout/InitiativeViewPageLayout'
import { useEffect, useState } from 'react'
import { useClient, useAuthState } from '@based/sdk'
import { useTranslations } from '@i18n/useLanguage'
import { useLocation } from 'wouter'
import { pages } from '@app/util/links'
import { Title } from '@app/components/core/typography/Title'
import { Tabs } from '@app/components/core/tabs/Tabs'
import { useParamState } from '@app/util/hooks/useParamState'
import { INITIATIVE_USER_ROLES, InitiativeUserRole } from '@schema/initiative'
import { match } from 'ts-pattern'
import { InitiativeUser } from '../sections/InitiativeUser'
import { Action } from 'functions/app/dashboard/initiative/manage-participant'
import {
  DashboardInitiativeModel,
  DashboardInitiativeUser,
} from 'functions/app/dashboard/initiative/list-users'
import { fullName } from '@app/util/helpers'
import { useToast } from '@app/util/hooks/useToast'
import { useMessageBox } from '@app/util/hooks/useMessageBox'

const tabs = {
  members: 'members',
  followers: 'followers',
  admins: 'admins',
  requests: 'requests',
} as const
type Tab = keyof typeof tabs

export const InitiativePeople = ({
  initiativeId,
}: {
  initiativeId: string
}) => {
  const [view, setValue] = useParamState(tabs, tabs.members, 'tab')
  const client = useClient()
  const authState = useAuthState()
  const [_, navigate] = useLocation()
  const translationsRoot = useTranslations().app
  const translations = translationsRoot.dashboard.initiative
  const [users, setUsers] = useState<DashboardInitiativeUser[]>([])
  const [initiativeTitle, setInitiativeTitle] = useState<string>(null)
  const { showMessageBox } = useMessageBox()
  const { showFailToast, showSuccessToast } = useToast()
  useEffect(() => {
    client
      .query('list-initiative-users', {
        initiativeId: Number(initiativeId),
        userId: authState.userId,
      })
      .subscribe(
        (result: DashboardInitiativeModel) => {
          setInitiativeTitle(result.title ?? '')
          setUsers(result.users)
        },
        () => navigate(pages.dashboardInitiatives),
      )
  }, [client, initiativeId, authState, setUsers, navigate])

  const manage = async (userId: number, action: Action): Promise<void> => {
    await client
      .call('initiative-manage-participant', {
        initiativeId: Number(initiativeId),
        userId,
        action,
      })
      .then(() => {
        showSuccessToast(translations.people.participantStatusChanged)
      })
      .catch(() => {
        showFailToast()
      })
  }

  const handleAction = async (
    user: DashboardInitiativeUser,
    action: Action | 'message',
  ) => {
    if (action === 'message') {
      showMessageBox(
        users.map((u) => ({
          id: u.id,
          name: fullName(u),
          avatar: u.avatar,
          role: u.$role,
        })),
        Number(initiativeId),
        user.id,
      )
    } else {
      await manage(user.id, action)
    }
  }

  if (!initiativeTitle) {
    return (
      <InitiativeViewPageLayout>
        <Column alignItems="stretch" gap="1.5rem">
          <Breadcrumbs
            levels={[
              {
                label: translations.common.myInitiatives,
                onClick: () => navigate(pages.dashboardInitiatives),
              },
              {
                label: translations.common.loading,
              },
            ]}
          />
        </Column>
      </InitiativeViewPageLayout>
    )
  }

  const title =
    initiativeTitle.trim() !== ''
      ? initiativeTitle
      : translations.common.unnamedInitiative

  return (
    <InitiativeViewPageLayout>
      <Column alignItems="stretch" gap="1.5rem" style={{ flexGrow: 0 }}>
        <Breadcrumbs
          levels={[
            {
              label: translations.common.myInitiatives,
              onClick: () => navigate(pages.dashboardInitiatives),
            },
            {
              label: title,
              onClick: () => navigate(pages.dashboardInitiatives),
            },
            {
              label: translations.people.people,
            },
          ]}
        />
        <Column gap="0.5rem" alignItems="stretch" style={{ flexGrow: 0 }}>
          <Title.H3>{translations.people.people}</Title.H3>
          <Column alignItems="stretch">
            <Tabs.Group selectedTab={view}>
              <Tabs.Tab
                tabKey={tabs.members}
                label={translations.people.members}
                onSelectTab={() => setValue(tabs.members)}
              />
              <Tabs.Tab
                tabKey={tabs.followers}
                label={translations.people.followers}
                onSelectTab={() => setValue(tabs.followers)}
              />
              <Tabs.Tab
                tabKey={tabs.admins}
                label={translations.people.admins}
                onSelectTab={() => setValue(tabs.admins)}
              />
              <Tabs.Tab
                tabKey={tabs.requests}
                label={translations.people.requests}
                onSelectTab={() => setValue(tabs.requests)}
              />
            </Tabs.Group>
            {users
              .filter((u) => mapRole(view).includes(u.$role))
              .map((u) => (
                <InitiativeUser
                  user={u}
                  key={u.id}
                  onAction={(action) => handleAction(u, action)}
                />
              ))}
          </Column>
        </Column>
      </Column>
    </InitiativeViewPageLayout>
  )
}

const mapRole = (tab: Tab): InitiativeUserRole[] => {
  return match(tab)
    .with(tabs.members, () => [INITIATIVE_USER_ROLES.member])
    .with(tabs.followers, () => [INITIATIVE_USER_ROLES.follower])
    .with(tabs.admins, () => [
      INITIATIVE_USER_ROLES.admin,
      INITIATIVE_USER_ROLES.owner,
    ])
    .with(tabs.requests, () => [INITIATIVE_USER_ROLES.applicant])
    .exhaustive()
}
